import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../../shared/lib/supabase'
import { useGlobalActivity } from '../hooks/useGlobalActivity'

const PLAN_LABELS = {
  demo: 'Demo',
  design_partner: 'Design Partner',
  solo: 'Solo',
  advisory_firm: 'Advisory Firm',
  enterprise: 'Enterprise'
}

const DEFAULT_CLIENT_CAP = {
  demo: 2,
  design_partner: 15,
  solo: 10,
  advisory_firm: 50,
  enterprise: 999
}

const DEFAULT_PRICING = {
  demo: { platform: 0, per_client: 0 },
  design_partner: { platform: 350, per_client: 100 },
  solo: { platform: 750, per_client: 150 },
  advisory_firm: { platform: 1500, per_client: 125 },
  enterprise: { platform: 0, per_client: 0 }
}

function OrganizationDetail() {
  const { orgId } = useParams()
  const [org, setOrg] = useState(null)
  const [members, setMembers] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('user-management')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'org_member', full_name: '' })
  const [invitedUser, setInvitedUser] = useState(null) // { email, isNewUser }
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState('')
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [pricingForm, setPricingForm] = useState({ enabled: false, platform_fee: 0, per_client: 0 })
  const [savingPricing, setSavingPricing] = useState(false)
  const [pricingSaved, setPricingSaved] = useState(false)
  const [selectedMembers, setSelectedMembers] = useState(new Set())
  const [editingMember, setEditingMember] = useState(null)
  const [editMemberRole, setEditMemberRole] = useState('')
  const [savingMember, setSavingMember] = useState(false)
  const [deletingMembers, setDeletingMembers] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteFullAccount, setDeleteFullAccount] = useState(true)

  // User health modal state (must be before any early returns to satisfy Rules of Hooks)
  const [healthCheckUser, setHealthCheckUser] = useState(null)
  const [healthData, setHealthData] = useState(null)
  const [healthLoading, setHealthLoading] = useState(false)
  const [adminActionLoading, setAdminActionLoading] = useState(null)
  const [adminActionMessage, setAdminActionMessage] = useState('')
  const [showHealthModal, setShowHealthModal] = useState(false)
  const [healthModalTab, setHealthModalTab] = useState('overview')

  const { activities: orgActivities } = useGlobalActivity({ orgId, limit: 20 })

  const fetchOrgData = useCallback(async () => {
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      const token = session.access_token

      // Fetch org details
      const orgRes = await fetch(`/api/organizations/${orgId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (orgRes.ok) {
        const data = await orgRes.json()
        setOrg(data.organization)
        setEditForm(data.organization)
        const override = data.organization?.settings?.pricing_override
        setPricingForm({
          enabled: override?.enabled || false,
          platform_fee: override?.platform_fee ?? DEFAULT_PRICING[data.organization.plan_tier]?.platform ?? 0,
          per_client: override?.per_client ?? DEFAULT_PRICING[data.organization.plan_tier]?.per_client ?? 0
        })
      }

      // Fetch members — try embedded join (requires migration 016 FK), fallback to separate queries
      let membersData = []
      const { data: joinedMembers, error: joinErr } = await supabase
        .from('org_members')
        .select('*, profiles!org_members_user_id_profiles_fk(email, full_name, role, updated_at)')
        .eq('org_id', orgId)
        .order('created_at', { ascending: true })

      if (!joinErr) {
        membersData = joinedMembers || []
      } else {
        // Fallback: fetch members and profiles separately
        const { data: rawMembers } = await supabase
          .from('org_members')
          .select('*')
          .eq('org_id', orgId)
          .order('created_at', { ascending: true })

        const userIds = (rawMembers || []).map(m => m.user_id)
        let profileMap = {}
        if (userIds.length) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, email, full_name, role, updated_at')
            .in('id', userIds)
          profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]))
        }
        membersData = (rawMembers || []).map(m => ({
          ...m,
          profiles: profileMap[m.user_id] || null
        }))
      }

      setMembers(membersData)

      // Fetch last login dates from audit_logs
      const memberUserIds = membersData.map(m => m.user_id).filter(Boolean)
      if (memberUserIds.length) {
        const { data: loginLogs } = await supabase
          .from('audit_logs')
          .select('user_id, created_at')
          .in('user_id', memberUserIds)
          .order('created_at', { ascending: false })

        if (loginLogs?.length) {
          const lastLoginMap = {}
          for (const log of loginLogs) {
            if (!lastLoginMap[log.user_id]) lastLoginMap[log.user_id] = log.created_at
          }
          setMembers(prev => prev.map(m => ({
            ...m,
            lastLogin: lastLoginMap[m.user_id] || null
          })))
        }
      }

      // Fetch clients for this org
      const { data: clientsData } = await supabase
        .from('clients')
        .select('id, name, industry, status, created_at')
        .eq('org_id', orgId)
        .order('name')

      setClients(clientsData || [])
    } catch (err) {
      console.error('Error fetching org data:', err)
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    fetchOrgData()
  }, [fetchOrgData])

  const handleSaveEdit = async () => {
    setSaving(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`/api/organizations/${orgId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editForm.name,
          status: editForm.status,
          plan_tier: editForm.plan_tier,
          client_cap: parseInt(editForm.client_cap, 10),
          billing_email: editForm.billing_email
        })
      })
      if (res.ok) {
        const data = await res.json()
        setOrg(data.organization)
        setEditForm(data.organization)
        setEditing(false)
      } else {
        const errData = await res.json().catch(() => ({}))
        console.error('Save org failed:', res.status, errData)
        alert(errData.error || `Save failed (${res.status})`)
      }
    } catch (err) {
      console.error('Error saving org:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleSavePricing = async () => {
    setSavingPricing(true)
    setPricingSaved(false)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const updatedSettings = {
        ...(org.settings || {}),
        pricing_override: {
          enabled: pricingForm.enabled,
          platform_fee: parseFloat(pricingForm.platform_fee) || 0,
          per_client: parseFloat(pricingForm.per_client) || 0
        }
      }
      const res = await fetch(`/api/organizations/${orgId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ settings: updatedSettings })
      })
      if (res.ok) {
        const data = await res.json()
        setOrg(data.organization)
        setPricingSaved(true)
        setTimeout(() => setPricingSaved(false), 2000)
      }
    } catch (err) {
      console.error('Error saving pricing:', err)
    } finally {
      setSavingPricing(false)
    }
  }

  const handleInvite = async (e) => {
    e.preventDefault()
    setInviteError('')
    setInviting(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`/api/organizations/${orgId}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inviteForm)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Failed to invite member')
      setInvitedUser({ email: inviteForm.email, isNewUser: data.isNewUser !== false })
      setInviteForm({ email: '', role: 'org_member', full_name: '' })
      fetchOrgData()
    } catch (err) {
      setInviteError(err.message)
    } finally {
      setInviting(false)
    }
  }

  const handleInviteDone = () => {
    setInvitedUser(null)
    setShowInviteModal(false)
  }

  const handleToggleSelect = (memberId) => {
    setSelectedMembers(prev => {
      const next = new Set(prev)
      if (next.has(memberId)) next.delete(memberId)
      else next.add(memberId)
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedMembers.size === members.length) {
      setSelectedMembers(new Set())
    } else {
      setSelectedMembers(new Set(members.map(m => m.id)))
    }
  }

  const handleEditMember = (member) => {
    setEditingMember(member)
    setEditMemberRole(member.role)
  }

  const handleSaveMemberRole = async () => {
    if (!editingMember) return
    setSavingMember(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`/api/organizations/${orgId}/members/${editingMember.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: editMemberRole })
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to update')
      }
      setEditingMember(null)
      fetchOrgData()
    } catch (err) {
      console.error('Error updating member:', err)
    } finally {
      setSavingMember(false)
    }
  }

  const handleBulkDelete = async () => {
    setDeletingMembers(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()

      if (deleteFullAccount) {
        // Full account deletion — removes user from all orgs, clients, and auth
        const memberUserIds = members
          .filter(m => selectedMembers.has(m.id))
          .map(m => m.user_id)
          .filter(Boolean)

        const promises = memberUserIds.map(userId =>
          fetch(`/api/users/${userId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          })
        )
        await Promise.all(promises)
      } else {
        // Remove from org only — keeps auth account intact
        const promises = [...selectedMembers].map(memberId =>
          fetch(`/api/organizations/${orgId}/members/${memberId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          })
        )
        await Promise.all(promises)
      }

      setSelectedMembers(new Set())
      setShowDeleteConfirm(false)
      setDeleteFullAccount(true)
      fetchOrgData()
    } catch (err) {
      console.error('Error deleting members:', err)
    } finally {
      setDeletingMembers(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!org) {
    return (
      <div className="card p-12 text-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Organization not found</h3>
        <Link to="/global/organizations" className="btn-primary">Back to Organizations</Link>
      </div>
    )
  }

  const tabs = [
    { key: 'user-management', label: `User Management (${members.length})` },
    { key: 'clients', label: `Clients (${clients.length})` },
    { key: 'pricing', label: 'Pricing' },
    { key: 'activity', label: 'Activity' }
  ]

  const fetchUserHealth = async (member) => {
    setHealthCheckUser(member)
    setHealthData(null)
    setHealthLoading(true)
    setAdminActionMessage('')
    setShowHealthModal(true)
    setHealthModalTab('overview')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`/api/users/${member.user_id}/health`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      })
      if (res.ok) {
        setHealthData(await res.json())
      }
    } catch (err) {
      console.error('Health check failed:', err)
    } finally {
      setHealthLoading(false)
    }
  }

  const performAdminAction = async (action, label) => {
    if (!healthCheckUser) return
    setAdminActionLoading(action)
    setAdminActionMessage('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`/api/users/${healthCheckUser.user_id}/admin-action`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      })
      const data = await res.json()
      if (res.ok) {
        if (data.tempPassword) {
          setAdminActionMessage(`${label}: ${data.message}. Temporary password: ${data.tempPassword}`)
        } else {
          setAdminActionMessage(`${label}: ${data.message}`)
        }
        fetchUserHealth(healthCheckUser)
      } else {
        setAdminActionMessage(`Error: ${data.error}`)
      }
    } catch (err) {
      setAdminActionMessage(`Error: ${err.message}`)
    } finally {
      setAdminActionLoading(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Link to="/global/organizations" className="hover:text-primary-600 dark:hover:text-accent-300">Organizations</Link>
        <span>/</span>
        <span className="text-gray-900 dark:text-white">{org.name}</span>
      </div>

      {/* Org Header Card */}
      <div className="card p-6">
        {editing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <select
                  value={editForm.status || 'active'}
                  onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="trial">Trial</option>
                  <option value="suspended">Suspended</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plan Tier</label>
                <select
                  value={editForm.plan_tier || 'design_partner'}
                  onChange={(e) => {
                    const tier = e.target.value
                    setEditForm(prev => ({ ...prev, plan_tier: tier, client_cap: DEFAULT_CLIENT_CAP[tier] || 15 }))
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="demo">Demo</option>
                  <option value="design_partner">Design Partner</option>
                  <option value="solo">Solo</option>
                  <option value="advisory_firm">Advisory Firm</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Client Cap</label>
                <input
                  type="number"
                  value={editForm.client_cap || 15}
                  onChange={(e) => setEditForm(prev => ({ ...prev, client_cap: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  min={1}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleSaveEdit} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => { setEditing(false); setEditForm(org) }} className="btn-secondary">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{org.name}</h1>
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                org.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                : org.status === 'trial' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                : org.status === 'suspended' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400'
              }`}>
                {org.status}
              </span>
              <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                {PLAN_LABELS[org.plan_tier] || org.plan_tier}
              </span>
            </div>
            <button onClick={() => setEditing(true)} className="btn-secondary">Edit</button>
          </div>
        )}
        {!editing && (
          <div className="mt-3 flex gap-6 text-sm text-gray-500 dark:text-gray-400">
            <span>Slug: <span className="font-mono">{org.slug}</span></span>
            <span>Clients: {clients.length} / {org.client_cap}</span>
            <span>Created: {new Date(org.created_at).toLocaleDateString()}</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'user-management' && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">User Management</h3>
            <div className="flex items-center gap-2">
              {selectedMembers.size === 1 && (
                <button
                  onClick={() => handleEditMember(members.find(m => selectedMembers.has(m.id)))}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Edit Role
                </button>
              )}
              {selectedMembers.size > 0 && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Remove ({selectedMembers.size})
                </button>
              )}
              <button onClick={() => setShowInviteModal(true)} className="btn-primary">Invite Member</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={members.length > 0 && selectedMembers.size === members.length}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Joined</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Activity</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {members.map(member => (
                  <tr key={member.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedMembers.has(member.id) ? 'bg-primary-50 dark:bg-primary-900/10' : ''}`}>
                    <td className="px-4 py-4 w-10">
                      <input
                        type="checkbox"
                        checked={selectedMembers.has(member.id)}
                        onChange={() => handleToggleSelect(member.id)}
                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {member.profiles?.full_name?.[0] || member.profiles?.email?.[0]?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); fetchUserHealth(member) }}
                          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline decoration-primary-300 dark:decoration-primary-700 underline-offset-2 hover:decoration-primary-500 dark:hover:decoration-primary-400 transition-colors text-left"
                        >
                          {member.profiles?.full_name || 'Unnamed'}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500 dark:text-gray-400">{member.profiles?.email}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        member.role === 'global_admin' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400'
                        : member.role === 'org_admin' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400'
                      }`}>
                        {member.role === 'org_admin' ? 'Org Admin' : member.role === 'org_member' ? 'Org Member' : member.role}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(member.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {member.lastLogin
                          ? <>
                              {new Date(member.lastLogin).toLocaleDateString()}
                              <span className="block text-xs text-gray-400 dark:text-gray-500">
                                {new Date(member.lastLogin).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                              </span>
                            </>
                          : <span className="italic text-gray-400 dark:text-gray-500">Never</span>
                        }
                      </span>
                    </td>
                    <td className="px-4 py-4 w-10">
                      <button
                        onClick={() => handleEditMember(member)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        title="Edit role"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
                {members.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No members yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Member Role Modal */}
      {editingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Edit Member Role</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {editingMember.profiles?.full_name || editingMember.profiles?.email || 'User'}
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
              <select
                value={editMemberRole}
                onChange={(e) => setEditMemberRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="org_member">Org Member</option>
                <option value="org_admin">Org Admin</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditingMember(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleSaveMemberRole} disabled={savingMember || editMemberRole === editingMember.role} className="btn-primary">
                {savingMember ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Remove {selectedMembers.size} Member{selectedMembers.size > 1 ? 's' : ''}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Choose how to handle the account{selectedMembers.size > 1 ? 's' : ''}.</p>
              </div>
            </div>

            <div className="space-y-2 mb-5">
              <label
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${deleteFullAccount ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                onClick={() => setDeleteFullAccount(true)}
              >
                <input type="radio" checked={deleteFullAccount} onChange={() => setDeleteFullAccount(true)} className="mt-0.5 text-red-600 focus:ring-red-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Delete account entirely</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Removes from all organizations, deletes auth credentials, and cleans up all associated data. Cannot be undone.</p>
                </div>
              </label>
              <label
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${!deleteFullAccount ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                onClick={() => setDeleteFullAccount(false)}
              >
                <input type="radio" checked={!deleteFullAccount} onChange={() => setDeleteFullAccount(false)} className="mt-0.5 text-amber-600 focus:ring-amber-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Remove from this organization only</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Revokes access to this organization but keeps the account active. The user can still access other organizations they belong to.</p>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowDeleteConfirm(false); setDeleteFullAccount(true) }} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={deletingMembers}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {deletingMembers ? 'Processing...' : deleteFullAccount ? 'Delete Account' : 'Remove from Org'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'clients' && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Clients</h3>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div
                className="h-2 rounded-full bg-primary-500"
                style={{ width: `${Math.min((clients.length / (org.client_cap || 1)) * 100, 100)}%` }}
              />
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
              {clients.length} / {org.client_cap}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Client Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Industry</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {clients.map(client => (
                  <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{client.name}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500 dark:text-gray-400">{client.industry || '-'}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        client.status === 'discovery' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400'
                        : client.status === 'retainer' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400'
                      }`}>
                        {client.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(client.created_at).toLocaleDateString()}
                      </span>
                    </td>
                  </tr>
                ))}
                {clients.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No clients yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'pricing' && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Pricing Override</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Override the default plan pricing for this organization. When disabled, standard {PLAN_LABELS[org.plan_tier] || org.plan_tier} pricing applies.
          </p>

          {/* Default pricing reference */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mb-6">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Default Plan Pricing ({PLAN_LABELS[org.plan_tier] || org.plan_tier})</div>
            <div className="flex gap-6 text-sm">
              <span className="text-gray-700 dark:text-gray-300">
                Platform Fee: <span className="font-semibold">${(DEFAULT_PRICING[org.plan_tier]?.platform ?? 0).toLocaleString()}/mo</span>
              </span>
              <span className="text-gray-700 dark:text-gray-300">
                Per Client: <span className="font-semibold">${(DEFAULT_PRICING[org.plan_tier]?.per_client ?? 0).toLocaleString()}/mo</span>
              </span>
            </div>
          </div>

          {/* Override toggle */}
          <div className="flex items-center gap-3 mb-6">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={pricingForm.enabled}
                onChange={(e) => setPricingForm(prev => ({ ...prev, enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-500"></div>
            </label>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable custom pricing</span>
            {pricingForm.enabled && (
              <span className="text-xs text-amber-600 dark:text-amber-400">Overrides default plan pricing</span>
            )}
          </div>

          {/* Override fields */}
          <div className={`space-y-4 max-w-md ${!pricingForm.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Platform Fee ($/mo)</label>
              <input
                type="number"
                value={pricingForm.platform_fee}
                onChange={(e) => setPricingForm(prev => ({ ...prev, platform_fee: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                min={0}
                step={1}
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Set to 0 for no platform fee</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Per Client Fee ($/mo)</label>
              <input
                type="number"
                value={pricingForm.per_client}
                onChange={(e) => setPricingForm(prev => ({ ...prev, per_client: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                min={0}
                step={1}
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Set to 0 for no per-client fee</p>
            </div>

            {/* Preview */}
            {pricingForm.enabled && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mt-4">
                <div className="text-xs font-medium text-green-700 dark:text-green-400 uppercase tracking-wider mb-1">Estimated MRR with {clients.length} client{clients.length !== 1 ? 's' : ''}</div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  ${((parseFloat(pricingForm.platform_fee) || 0) + (parseFloat(pricingForm.per_client) || 0) * clients.length).toLocaleString()}/mo
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button onClick={handleSavePricing} disabled={savingPricing} className="btn-primary">
              {savingPricing ? 'Saving...' : 'Save Pricing'}
            </button>
            {pricingSaved && (
              <span className="text-sm text-green-600 dark:text-green-400">Pricing saved</span>
            )}
          </div>
        </div>
      )}

      {/* User Health Modal */}
      {showHealthModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowHealthModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>

            {/* User identity header */}
            <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-750 px-6 py-5 rounded-t-xl border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-gray-700 shadow-md">
                    <span className="text-white font-semibold text-base">
                      {(healthCheckUser?.profiles?.full_name?.[0] || healthCheckUser?.profiles?.email?.[0] || 'U').toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {healthCheckUser?.profiles?.full_name || 'Unnamed'}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{healthCheckUser?.profiles?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {healthData && (
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                      healthData.auth?.lastSignInAt ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                    }`}>
                      {healthData.auth?.lastSignInAt ? 'Active' : 'Never Signed In'}
                    </span>
                  )}
                  <button
                    onClick={() => setShowHealthModal(false)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Status message */}
              {adminActionMessage && (
                <div className={`mt-3 text-sm px-3 py-2 rounded-lg ${adminActionMessage.startsWith('Error') ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800' : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'}`}>
                  {adminActionMessage}
                </div>
              )}

              {/* Tab navigation */}
              {healthData && (
                <div className="flex gap-1 mt-4 -mb-5 px-0">
                  {[
                    { key: 'overview', label: 'Overview', icon: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z' },
                    { key: 'security', label: 'Security', icon: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z' },
                    { key: 'activity', label: 'Activity', icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z' },
                    { key: 'errors', label: `Errors${healthData.clientErrors?.length ? ` (${healthData.clientErrors.length})` : ''}`, icon: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z' },
                    { key: 'actions', label: 'Actions', icon: 'M11.42 15.17l-5.1-5.1m0 0L11.42 4.97m-5.1 5.1h11.25M4.5 12a7.5 7.5 0 0015 0 7.5 7.5 0 00-15 0z' }
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setHealthModalTab(tab.key)}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                        healthModalTab === tab.key
                          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 border-b-white dark:border-b-gray-800 shadow-sm'
                          : tab.key === 'errors' && healthData.clientErrors?.length > 0
                            ? 'text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={tab.icon} />
                      </svg>
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Modal body */}
            <div className="px-6 py-5 overflow-y-auto scrollbar-hide flex-1">
              {healthLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : healthData ? (
                <>
                  {/* Overview Tab */}
                  {healthModalTab === 'overview' && (
                    <div className="space-y-5">
                      {/* Quick status row */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-center">
                          <div className={`inline-flex w-8 h-8 rounded-full items-center justify-center mb-2 ${
                            healthData.auth?.emailConfirmedAt ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
                          }`}>
                            <svg className={`w-4 h-4 ${healthData.auth?.emailConfirmedAt ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={healthData.auth?.emailConfirmedAt ? 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' : 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'} />
                            </svg>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                          <p className={`text-sm font-medium ${healthData.auth?.emailConfirmedAt ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                            {healthData.auth?.emailConfirmedAt ? 'Confirmed' : 'Pending'}
                          </p>
                        </div>
                        <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-center">
                          <div className={`inline-flex w-8 h-8 rounded-full items-center justify-center mb-2 ${
                            healthData.mfa?.status === 'enrolled' ? 'bg-green-100 dark:bg-green-900/30' : healthData.mfa?.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-red-100 dark:bg-red-900/30'
                          }`}>
                            <svg className={`w-4 h-4 ${healthData.mfa?.status === 'enrolled' ? 'text-green-600 dark:text-green-400' : healthData.mfa?.status === 'pending' ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                            </svg>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">MFA</p>
                          <p className={`text-sm font-medium ${healthData.mfa?.status === 'enrolled' ? 'text-green-600 dark:text-green-400' : healthData.mfa?.status === 'pending' ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                            {healthData.mfa?.status === 'enrolled' ? 'Enrolled' : healthData.mfa?.status === 'pending' ? 'Pending' : 'Not Enrolled'}
                          </p>
                        </div>
                        <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-center">
                          <div className={`inline-flex w-8 h-8 rounded-full items-center justify-center mb-2 ${
                            !healthData.profile?.must_change_password ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
                          }`}>
                            <svg className={`w-4 h-4 ${!healthData.profile?.must_change_password ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                            </svg>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Password</p>
                          <p className={`text-sm font-medium ${!healthData.profile?.must_change_password ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                            {healthData.profile?.must_change_password ? 'Change Required' : 'OK'}
                          </p>
                        </div>
                      </div>

                      {/* Onboarding Pipeline */}
                      {healthData.onboarding && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Onboarding Pipeline</h4>
                          <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4">
                            <div className="flex items-center justify-between">
                              {healthData.onboarding.steps.map((step, i) => {
                                const isCurrent = healthData.onboarding.currentStep === step.key
                                return (
                                  <div key={step.key} className="flex items-center flex-1">
                                    <div className="flex flex-col items-center flex-shrink-0">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                                        step.complete
                                          ? 'bg-green-100 dark:bg-green-900/30 border-green-500 dark:border-green-400'
                                          : isCurrent
                                            ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-500 dark:border-amber-400 animate-pulse'
                                            : 'bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500'
                                      }`}>
                                        {step.complete ? (
                                          <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                          </svg>
                                        ) : isCurrent ? (
                                          <div className="w-2.5 h-2.5 rounded-full bg-amber-500 dark:bg-amber-400" />
                                        ) : (
                                          <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-500" />
                                        )}
                                      </div>
                                      <span className={`text-[10px] mt-1.5 font-medium text-center leading-tight ${
                                        step.complete ? 'text-green-600 dark:text-green-400'
                                        : isCurrent ? 'text-amber-600 dark:text-amber-400'
                                        : 'text-gray-400 dark:text-gray-500'
                                      }`}>
                                        {step.label}
                                      </span>
                                    </div>
                                    {i < healthData.onboarding.steps.length - 1 && (
                                      <div className={`flex-1 h-0.5 mx-2 mt-[-16px] rounded-full ${
                                        step.complete ? 'bg-green-400 dark:bg-green-600' : 'bg-gray-200 dark:bg-gray-600'
                                      }`} />
                                    )}
                                  </div>
                                )
                              })}
                              {/* Active terminal node */}
                              <div className="flex flex-col items-center flex-shrink-0">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                                  healthData.onboarding.currentStep === 'active'
                                    ? 'bg-green-100 dark:bg-green-900/30 border-green-500 dark:border-green-400'
                                    : 'bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500'
                                }`}>
                                  {healthData.onboarding.currentStep === 'active' ? (
                                    <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-500" />
                                  )}
                                </div>
                                <span className={`text-[10px] mt-1.5 font-medium ${
                                  healthData.onboarding.currentStep === 'active' ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'
                                }`}>Active</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Account details */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Account Details</h4>
                        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl divide-y divide-gray-200 dark:divide-gray-700">
                          <div className="flex items-center justify-between px-4 py-3">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Platform Role</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{(healthData.profile?.role || 'unknown').replace(/_/g, ' ')}</span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-3">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Account Created</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {healthData.auth?.createdAt ? new Date(healthData.auth.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-3">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Last Sign In</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {healthData.auth?.lastSignInAt
                                ? <>
                                    {new Date(healthData.auth.lastSignInAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-1.5">{new Date(healthData.auth.lastSignInAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                                  </>
                                : <span className="text-gray-400 dark:text-gray-500 italic">Never</span>
                              }
                            </span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-3">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Email Confirmed</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {healthData.auth?.emailConfirmedAt
                                ? new Date(healthData.auth.emailConfirmedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : <span className="text-amber-600 dark:text-amber-400">Not confirmed</span>
                              }
                            </span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-3">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Auth Provider</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{healthData.auth?.provider || 'email'}</span>
                          </div>
                          {healthData.auth?.invitedAt && (
                            <div className="flex items-center justify-between px-4 py-3">
                              <span className="text-sm text-gray-500 dark:text-gray-400">Invite Sent</span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {new Date(healthData.auth.invitedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-1.5">{new Date(healthData.auth.invitedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                              </span>
                            </div>
                          )}
                          {healthData.auth?.recoverySentAt && (
                            <div className="flex items-center justify-between px-4 py-3">
                              <span className="text-sm text-gray-500 dark:text-gray-400">Recovery Email Sent</span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {new Date(healthData.auth.recoverySentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-1.5">{new Date(healthData.auth.recoverySentAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Org memberships */}
                      {healthData.memberships?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Organizations</h4>
                          <div className="space-y-2">
                            {healthData.memberships.map((m, i) => (
                              <div key={i} className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-7 h-7 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                                    <span className="text-primary-700 dark:text-primary-400 text-xs font-semibold">{m.orgName?.[0]?.toUpperCase()}</span>
                                  </div>
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">{m.orgName}</span>
                                </div>
                                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                  m.role === 'org_admin' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                                }`}>
                                  {m.role?.replace(/_/g, ' ')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Security Tab */}
                  {healthModalTab === 'security' && (
                    <div className="space-y-5">
                      {/* MFA */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Multi-Factor Authentication</h4>
                        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                healthData.mfa?.status === 'enrolled' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                              }`}>
                                <svg className={`w-5 h-5 ${healthData.mfa?.status === 'enrolled' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                                </svg>
                              </div>
                              <div>
                                <p className={`text-sm font-medium ${healthData.mfa?.status === 'enrolled' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                                  {healthData.mfa?.status === 'enrolled' ? 'MFA Enrolled' : healthData.mfa?.status === 'pending' ? 'MFA Pending Verification' : 'MFA Not Enrolled'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {healthData.mfa?.factors?.length > 0 ? `${healthData.mfa.factors.length} factor${healthData.mfa.factors.length !== 1 ? 's' : ''} registered` : 'No factors registered'}
                                </p>
                              </div>
                            </div>
                            {healthData.mfa?.status !== 'not_enrolled' && (
                              <button
                                onClick={() => {
                                  if (window.confirm('This will unenroll all MFA factors. The user will need to re-enroll on next login. Continue?')) {
                                    performAdminAction('unenroll_mfa', 'Unenroll MFA')
                                  }
                                }}
                                disabled={!!adminActionLoading}
                                className="text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors disabled:opacity-50"
                              >
                                {adminActionLoading === 'unenroll_mfa' ? 'Unenrolling...' : 'Unenroll'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Password */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Password</h4>
                        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                !healthData.profile?.must_change_password ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
                              }`}>
                                <svg className={`w-5 h-5 ${!healthData.profile?.must_change_password ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                                </svg>
                              </div>
                              <div>
                                <p className={`text-sm font-medium ${!healthData.profile?.must_change_password ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
                                  {healthData.profile?.must_change_password ? 'Password Change Required' : 'Password OK'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {healthData.profile?.must_change_password ? 'User must change password on next login' : 'No pending password requirements'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Email verification */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Email Verification</h4>
                        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              healthData.auth?.emailConfirmedAt ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
                            }`}>
                              <svg className={`w-5 h-5 ${healthData.auth?.emailConfirmedAt ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                              </svg>
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${healthData.auth?.emailConfirmedAt ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
                                {healthData.auth?.emailConfirmedAt ? 'Email Verified' : 'Email Not Verified'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {healthData.auth?.emailConfirmedAt
                                  ? `Confirmed on ${new Date(healthData.auth.emailConfirmedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                                  : 'User has not confirmed their email address'
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Activity Tab */}
                  {healthModalTab === 'activity' && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Recent Activity (7 days)</h4>
                        <span className="text-xs text-gray-400 dark:text-gray-500">{healthData.recentEvents?.length || 0} events</span>
                      </div>
                      {healthData.recentEvents?.length > 0 ? (
                        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl divide-y divide-gray-200 dark:divide-gray-700 max-h-[50vh] overflow-y-auto scrollbar-hide">
                          {healthData.recentEvents.map(e => (
                            <div key={e.id} className="flex items-start gap-3 px-4 py-3">
                              <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <svg className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-900 dark:text-white">{e.description || e.action}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    {new Date(e.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(e.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                  </span>
                                  {e.category && (
                                    <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-300">{e.category}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-8 text-center">
                          <svg className="w-10 h-10 mx-auto mb-2 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm text-gray-500 dark:text-gray-400">No activity in the last 7 days</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Errors Tab */}
                  {healthModalTab === 'errors' && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Client Error Reports (30 days)</h4>
                        <span className="text-xs text-gray-400 dark:text-gray-500">{healthData.clientErrors?.length || 0} errors</span>
                      </div>
                      {healthData.clientErrors?.length > 0 ? (
                        <div className="space-y-3 max-h-[50vh] overflow-y-auto scrollbar-hide">
                          {healthData.clientErrors.map(err => (
                            <div key={err.id} className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 rounded-xl p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 uppercase tracking-wider">
                                    {err.action?.replace('client_error.', '') || 'error'}
                                  </span>
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    {new Date(err.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(err.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                  </span>
                                </div>
                              </div>

                              {/* Error message */}
                              <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-2">{err.description}</p>

                              {/* Error details */}
                              {err.metadata && (
                                <div className="space-y-2">
                                  {(err.metadata.error_code || err.metadata.error_status) && (
                                    <div className="flex gap-3 text-xs">
                                      {err.metadata.error_code && (
                                        <span className="text-gray-600 dark:text-gray-400">
                                          Code: <span className="font-mono font-medium text-red-700 dark:text-red-400">{err.metadata.error_code}</span>
                                        </span>
                                      )}
                                      {err.metadata.error_status && (
                                        <span className="text-gray-600 dark:text-gray-400">
                                          Status: <span className="font-mono font-medium text-red-700 dark:text-red-400">{err.metadata.error_status}</span>
                                        </span>
                                      )}
                                      {err.metadata.error_name && (
                                        <span className="text-gray-600 dark:text-gray-400">
                                          Type: <span className="font-mono font-medium text-red-700 dark:text-red-400">{err.metadata.error_name}</span>
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  {/* Stack trace (collapsible) */}
                                  {err.metadata.stack && (
                                    <details className="group">
                                      <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 select-none">
                                        Stack trace
                                      </summary>
                                      <pre className="mt-1.5 text-[10px] leading-relaxed font-mono text-gray-600 dark:text-gray-400 bg-gray-900/5 dark:bg-black/20 rounded-lg p-3 overflow-x-auto max-h-32 scrollbar-hide whitespace-pre-wrap break-all">
                                        {err.metadata.stack}
                                      </pre>
                                    </details>
                                  )}

                                  {/* Browser info (collapsible) */}
                                  {err.metadata.browser && (
                                    <details className="group">
                                      <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 select-none">
                                        Browser details
                                      </summary>
                                      <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] text-gray-600 dark:text-gray-400 bg-gray-900/5 dark:bg-black/20 rounded-lg p-3">
                                        <span>Platform: <span className="font-medium">{err.metadata.browser.platform}</span></span>
                                        <span>Online: <span className="font-medium">{err.metadata.browser.online ? 'Yes' : 'No'}</span></span>
                                        <span>Viewport: <span className="font-medium">{err.metadata.browser.viewportWidth}x{err.metadata.browser.viewportHeight}</span></span>
                                        <span>Screen: <span className="font-medium">{err.metadata.browser.screenWidth}x{err.metadata.browser.screenHeight}</span></span>
                                        <span>Language: <span className="font-medium">{err.metadata.browser.language}</span></span>
                                        <span>Timezone: <span className="font-medium">{err.metadata.browser.timezone}</span></span>
                                        <span className="col-span-2">UA: <span className="font-medium break-all">{err.metadata.browser.userAgent}</span></span>
                                      </div>
                                    </details>
                                  )}

                                  {/* URL */}
                                  {err.metadata.url && (
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                      URL: <span className="font-mono">{err.metadata.url}</span>
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-8 text-center">
                          <svg className="w-10 h-10 mx-auto mb-2 text-green-300 dark:text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm text-gray-500 dark:text-gray-400">No client errors reported in the last 30 days</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions Tab */}
                  {healthModalTab === 'actions' && (
                    <div className="space-y-4">
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Admin Actions</h4>

                      <div className="space-y-3">
                        {/* Copy Welcome Link */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" /></svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">Copy Welcome Link</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Copy the onboarding URL to share directly with the user</p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const email = healthCheckUser?.profiles?.email
                              if (!email) return
                              const url = `${window.location.origin}/welcome?email=${encodeURIComponent(email)}`
                              navigator.clipboard.writeText(url).then(() => {
                                setAdminActionMessage(`Welcome link copied to clipboard: ${url}`)
                                setTimeout(() => setAdminActionMessage(''), 4000)
                              }).catch(() => {
                                setAdminActionMessage('Unable to copy. URL: ' + url)
                              })
                            }}
                            disabled={!!adminActionLoading || !healthCheckUser?.profiles?.email}
                            className="w-24 px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
                          >
                            Copy
                          </button>
                        </div>

                        {/* Resend Sign-in Code */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">Resend Sign-in Code</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Email a new 6-digit OTP to this user. Existing codes will be invalidated.</p>
                            </div>
                          </div>
                          <button
                            onClick={() => performAdminAction('resend_otp', 'Resend Sign-in Code')}
                            disabled={!!adminActionLoading}
                            className="w-24 px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
                          >
                            {adminActionLoading === 'resend_otp' ? 'Sending...' : 'Send'}
                          </button>
                        </div>

                        {/* Send Password Reset */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" /></svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">Send Password Reset</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Email a password reset link to the user</p>
                            </div>
                          </div>
                          <button
                            onClick={() => performAdminAction('send_password_reset', 'Password Reset')}
                            disabled={!!adminActionLoading}
                            className="w-24 px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
                          >
                            {adminActionLoading === 'send_password_reset' ? 'Sending...' : 'Send'}
                          </button>
                        </div>

                        {/* Force Password Change */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">Force Password Change</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Require the user to set a new password on next login</p>
                            </div>
                          </div>
                          <button
                            onClick={() => performAdminAction('force_password_change', 'Force Password Change')}
                            disabled={!!adminActionLoading}
                            className="w-24 px-4 py-2 text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors disabled:opacity-50"
                          >
                            {adminActionLoading === 'force_password_change' ? 'Setting...' : 'Force'}
                          </button>
                        </div>

                        {/* Set Temporary Password */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">Set Temporary Password</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Generate a temp password for the user to log in with (bypasses email)</p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              if (window.confirm('This will set a temporary password and require the user to change it on next login. Continue?')) {
                                performAdminAction('set_temp_password', 'Temporary Password')
                              }
                            }}
                            disabled={!!adminActionLoading}
                            className="w-24 px-4 py-2 text-sm font-medium text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors disabled:opacity-50"
                          >
                            {adminActionLoading === 'set_temp_password' ? 'Setting...' : 'Generate'}
                          </button>
                        </div>

                        {/* Unenroll MFA */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">Unenroll MFA</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Remove all MFA factors; user will re-enroll on next login</p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              if (window.confirm('This will unenroll all MFA factors. The user will need to re-enroll on next login. Continue?')) {
                                performAdminAction('unenroll_mfa', 'Unenroll MFA')
                              }
                            }}
                            disabled={!!adminActionLoading || healthData.mfa?.status === 'not_enrolled'}
                            className="w-24 px-4 py-2 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                          >
                            {adminActionLoading === 'unenroll_mfa' ? 'Removing...' : 'Unenroll'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Activity</h3>
          <div className="space-y-3">
            {orgActivities.length > 0 ? orgActivities.map(activity => (
              <div key={activity.id} className="flex items-center gap-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-medium text-xs">
                    {activity.user_name?.[0] || activity.user_email?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-900 dark:text-white">
                    <span className="font-medium">{activity.user_name || activity.user_email}</span>
                    {' — '}
                    <span className="text-gray-600 dark:text-gray-400">{activity.action}</span>
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {new Date(activity.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            )) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No activity recorded</p>
            )}
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            {invitedUser ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">{invitedUser.isNewUser ? 'Invitation Sent' : 'Member Added'}</h2>
                </div>
                {invitedUser.isNewUser ? (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                      An invitation email has been sent to <span className="font-semibold text-gray-900 dark:text-white">{invitedUser.email}</span>. They will click the link, request a 6-digit code on the welcome page, then set a password and enroll in MFA.
                    </p>
                    <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                        Welcome URL (fallback)
                      </label>
                      <a href={`${window.location.origin}/welcome?email=${encodeURIComponent(invitedUser.email)}`} target="_blank" rel="noopener noreferrer" className="text-sm font-mono text-primary-600 dark:text-primary-400 hover:underline break-all">
                        {`${window.location.origin}/welcome?email=${encodeURIComponent(invitedUser.email)}`}
                      </a>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                        If the email doesn't arrive, share this URL with the user directly.
                      </p>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                    <span className="font-semibold text-gray-900 dark:text-white">{invitedUser.email}</span> has been added to this organization. They already have an account and can sign in with their existing credentials.
                  </p>
                )}
                <div className="flex justify-end">
                  <button onClick={handleInviteDone} className="btn-primary">Done</button>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Invite Member</h2>
                  <button onClick={() => { setShowInviteModal(false); setInviteError('') }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-800 dark:text-blue-300">
                  An invitation email will be sent. The user will request a 6-digit sign-in code on the welcome page, then set a password and enroll in MFA.
                </div>
                <form onSubmit={handleInvite} className="space-y-4">
                  {inviteError && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                      {inviteError}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                    <input
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name</label>
                    <input
                      type="text"
                      value={inviteForm.full_name}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, full_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
                    <select
                      value={inviteForm.role}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="org_member">Org Member</option>
                      <option value="org_admin">Org Admin</option>
                    </select>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button type="button" onClick={() => { setShowInviteModal(false); setInviteError('') }} className="btn-secondary">Cancel</button>
                    <button type="submit" disabled={inviting} className="btn-primary">
                      {inviting ? 'Sending invite...' : 'Send Invitation'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default OrganizationDetail
